import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, FileText, Shield, Scale } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

const pdfStyles = `
  .legal-pdf-mode {
    background: #ffffff !important;
    color: #1a1a1a !important;
    padding: 20px !important;
  }
  .legal-pdf-mode * {
    color: #1a1a1a !important;
    border-color: #d4d4d8 !important;
  }
  .legal-pdf-mode .pdf-brand-header { display: flex !important; }
  .legal-pdf-mode h1, .legal-pdf-mode h2, .legal-pdf-mode h3 { color: #18181b !important; }
  .legal-pdf-mode .legal-section {
    background: #f9fafb !important;
    border: 1px solid #d4d4d8 !important;
  }
  .legal-pdf-mode .legal-accent { color: #C41E3A !important; }
  .legal-pdf-mode .legal-badge {
    background: #f0f0f0 !important;
    color: #52525b !important;
  }
  .legal-pdf-mode .legal-green { color: #059669 !important; }
  .legal-pdf-mode .pdf-hide { display: none !important; }
  .legal-pdf-mode .pdf-footer {
    margin-top: 20px; padding-top: 12px;
    border-top: 1px solid #d4d4d8;
    text-align: center; font-size: 10px;
  }
  .legal-pdf-mode .pdf-footer * { color: #71717a !important; }
  .legal-pdf-mode .legal-divider {
    border-top: 2px solid #C41E3A !important;
    margin: 32px 0 !important;
    padding-top: 24px !important;
  }
  .legal-pdf-mode table { border-collapse: collapse; width: 100%; }
  .legal-pdf-mode th, .legal-pdf-mode td { border: 1px solid #d4d4d8; padding: 6px 10px; font-size: 12px; }
  .legal-pdf-mode th { background: #f4f4f5 !important; font-weight: 600; }
`;

const Section = ({ num, title, children }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 mb-4 legal-section">
    <h3 className="text-base font-bold text-white mt-0">{num}. {title}</h3>
    {children}
  </div>
);
const P = ({ children }) => <p className="text-zinc-300 mt-2 leading-relaxed text-sm">{children}</p>;
const UL = ({ children }) => <ul className="text-zinc-300 list-disc pl-5 mt-2 space-y-1 text-sm">{children}</ul>;

export default function LegalCompletePage() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const contentRef = useRef(null);

  const downloadPdf = async () => {
    if (!contentRef.current || generating) return;
    setGenerating(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = contentRef.current;
      el.classList.add('legal-pdf-mode');
      await html2pdf().set({
        margin: [10, 12, 10, 12],
        filename: 'BrandStage_Legal_Compliance_Complete.pdf',
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }).from(el).save();
      el.classList.remove('legal-pdf-mode');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <style>{pdfStyles}</style>

      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="legal-back-btn">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Go Back</span>
          </button>
          <button
            onClick={downloadPdf}
            disabled={generating}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="legal-download-pdf-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {generating ? 'Generating PDF...' : 'Download Complete PDF'}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 pt-24 pb-16">
        <div ref={contentRef}>

          {/* PDF-only brand header */}
          <div className="pdf-brand-header hidden items-center gap-3 mb-6 pb-4" style={{ borderBottom: '2px solid #C41E3A' }}>
            <img src={LOGO_URL} alt="BrandStage" style={{ height: '32px' }} crossOrigin="anonymous" />
            <div style={{ marginLeft: 'auto', fontSize: '10px', color: '#71717a', textAlign: 'right' }}>
              <span>BrandStage Legal & Compliance Pack</span><br />
              <span>Confidential — For Internal Use</span>
            </div>
          </div>

          {/* Cover / Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] px-4 py-1.5 rounded-full text-sm font-medium mb-4 legal-accent">
              <Scale className="w-4 h-4" />
              Legal & Compliance
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-3" data-testid="legal-complete-title">
              BrandStage — Complete Legal & Compliance Document
            </h1>
            <p className="text-zinc-400 text-sm max-w-2xl mx-auto">
              This document consolidates BrandStage's Terms of Service, Privacy Policy, Cookie Policy,
              Compliance & Data Protection Summary, and all product disclaimers into a single reference for internal legal review.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-500">
              <span>Operated by: Continex Tradeline India Pvt Ltd</span>
              <span>|</span>
              <span>Last updated: October 2026</span>
              <span>|</span>
              <span>Version 2.0</span>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 mb-8 legal-section">
            <h2 className="text-lg font-bold text-white mb-3">Table of Contents</h2>
            <div className="grid md:grid-cols-2 gap-1 text-sm">
              {[
                { icon: <FileText className="w-3.5 h-3.5" />, label: 'Part A — Terms of Service', sections: '17 sections' },
                { icon: <Shield className="w-3.5 h-3.5" />, label: 'Part B — Privacy Policy', sections: '18 sections' },
                { icon: <FileText className="w-3.5 h-3.5" />, label: 'Part C — Cookie Policy', sections: '6 sections' },
                { icon: <Scale className="w-3.5 h-3.5" />, label: 'Part D — Compliance & Data Protection', sections: '13 sections' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-zinc-300 py-1.5">
                  <span className="text-[#C41E3A] legal-accent">{item.icon}</span>
                  <span className="font-medium text-white">{item.label}</span>
                  <span className="text-zinc-500 ml-auto">{item.sections}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              PART A: TERMS OF SERVICE
              ═══════════════════════════════════════════════════════════════ */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#C41E3A]/30">
              <div className="bg-[#C41E3A]/20 p-2.5 rounded-lg"><FileText className="w-5 h-5 text-[#C41E3A]" /></div>
              <div>
                <h2 className="text-xl font-bold text-white">Part A — Terms of Service</h2>
                <p className="text-zinc-500 text-xs">Last updated: October 2026</p>
              </div>
            </div>

            <Section num="A.1" title="Acceptance of Terms">
              <P>By accessing or using BrandStage — including creating an account, subscribing to a plan, creating or viewing a profile, recording or uploading video content, collecting or providing testimonials, or using any feature on the platform — you agree to be bound by these Terms, our Privacy Policy, and our Cookie Policy. If you do not agree to these terms, please do not use our services.</P>
            </Section>
            <Section num="A.2" title="Services Description">
              <P>BrandStage is a video-first professional profile platform that helps businesses and professionals present themselves credibly to the wider world. Our services include but are not limited to:</P>
              <UL>
                <li><strong>Company & Leadership Profiles</strong> — AI-assisted creation, hosting, and management of private, link-only business profiles including company overview, key contact information, product/service showcases, and leadership narratives.</li>
                <li><strong>Video Recording & Processing</strong> — Professional video recording at events and trade shows, Quick Bite video recording (30–40 second branded clips), automated FFmpeg-based video branding, and cloud-hosted video delivery.</li>
                <li><strong>Guest Video Testimonials</strong> — Collection of video testimonials from your customers and partners via a shareable booth link, including consent capture, branding, and display on your profile.</li>
                <li><strong>Subscription Plans & Add-ons</strong> — Tiered subscription plans (Basic, All-Access) and individual Pro feature add-ons that unlock additional profile capabilities such as multi-language translations, email signatures, QR codes, event invitations, and downloadable resources.</li>
                <li><strong>Event Invitations</strong> — Creation and management of event invitations with RSVP tracking, sent to contacts via the platform.</li>
                <li><strong>Email Signatures & QR Codes</strong> — Generation of branded email signature blocks and scannable QR codes linked to your profile.</li>
                <li><strong>AI-Powered Features</strong> — AI-generated profile content, multi-language translations, and readiness scoring powered by third-party AI services.</li>
                <li><strong>Admin & Analytics</strong> — Dashboard tools for managing profiles, viewing analytics, processing payments, and tracking engagement.</li>
              </UL>
            </Section>
            <Section num="A.3" title="User Accounts & Registration">
              <P>To access most BrandStage services you must create an account. When registering you agree to:</P>
              <UL>
                <li>Provide accurate, current, and complete information about yourself and your organisation.</li>
                <li>Maintain and promptly update your registration information.</li>
                <li>Keep your password and account credentials confidential and secure.</li>
                <li>Accept responsibility for all activities that occur under your account.</li>
                <li>Notify us immediately of any unauthorised use of your account.</li>
              </UL>
              <P>We reserve the right to suspend or terminate accounts that contain inaccurate information or violate these Terms.</P>
            </Section>
            <Section num="A.4" title="Profile Services & Private Access">
              <P>BrandStage profiles are <strong>private by design</strong>. Your profile is not indexed by search engines and is not publicly discoverable. Only individuals who have your unique profile link can view your profile page.</P>
              <UL>
                <li>You are solely responsible for deciding who receives your profile link.</li>
                <li>You may update, modify, or unpublish your profile at any time through the profile editor.</li>
                <li>BrandStage does not share profile links with third parties unless explicitly authorised by you.</li>
                <li>Profile content must be accurate, lawful, and not misleading.</li>
                <li>BrandStage reserves the right to remove or disable profiles that violate these Terms.</li>
              </UL>
            </Section>
            <Section num="A.5" title="Subscription Plans, Payments & Renewals">
              <UL>
                <li><strong>Plans:</strong> BrandStage offers Basic and All-Access subscription tiers, as well as individual Pro feature add-ons. Features and pricing may change with reasonable notice.</li>
                <li><strong>Payments:</strong> All payments are processed securely through Razorpay. You agree to provide valid payment information.</li>
                <li><strong>Invoices:</strong> PDF invoices are generated automatically and sent to your registered email.</li>
                <li><strong>Renewals:</strong> Subscriptions renew at the end of each billing period unless cancelled before the renewal date.</li>
                <li><strong>Cancellation:</strong> You may cancel at any time. No pro-rata refunds for partially used billing periods.</li>
                <li><strong>Coupons:</strong> Promotional codes are subject to specific terms, expiry dates, and usage limits.</li>
                <li><strong>Price Changes:</strong> At least 30 days' advance notice for pricing adjustments.</li>
              </UL>
            </Section>
            <Section num="A.6" title="Content Rights & Intellectual Property">
              <UL>
                <li><strong>Your Ownership:</strong> You retain full ownership of your original content.</li>
                <li><strong>Licence to BrandStage:</strong> Non-exclusive, worldwide, royalty-free licence to host, store, process, display, and distribute your content solely for the purpose of providing the services.</li>
                <li><strong>AI-Generated Content:</strong> Provided as-is. You are responsible for reviewing and approving AI-generated content before publication.</li>
                <li><strong>BrandStage IP:</strong> The BrandStage name, logo, platform design, and proprietary technology remain the intellectual property of CONTINEX.</li>
                <li><strong>Third-Party Content:</strong> You confirm that you have all necessary rights for content you upload.</li>
              </UL>
            </Section>
            <Section num="A.7" title="Video Recording & Testimonial Consent">
              <UL>
                <li>You consent to being recorded and your video being processed, branded, and displayed as part of the agreed services.</li>
                <li>Processed videos may include BrandStage branding elements (intros, outros, lower thirds).</li>
                <li>Guest testimonial providers must give explicit consent before recording. Consent records (timestamp, IP) captured per DPDP/GDPR.</li>
                <li>You may request deletion of your videos at any time. Processed within 30 days.</li>
              </UL>
            </Section>
            <Section num="A.8" title="Data Protection & Privacy">
              <P>BrandStage is committed to protecting personal data in accordance with the Indian Digital Personal Data Protection Act (DPDP Act, 2023) and the EU GDPR where applicable. See Part B (Privacy Policy) for full details.</P>
            </Section>
            <Section num="A.9" title="Prohibited Uses">
              <UL>
                <li>Any unlawful purpose or promotion of illegal activities.</li>
                <li>Uploading harmful, offensive, defamatory, or inappropriate content.</li>
                <li>Infringing on intellectual property rights of others.</li>
                <li>Impersonating another person or organisation.</li>
                <li>Attempting to gain unauthorised access to systems, accounts, or data.</li>
                <li>Scraping, crawling, or harvesting data without written permission.</li>
                <li>Distributing spam, malware, or unsolicited communications.</li>
                <li>Reverse-engineering any part of the platform.</li>
              </UL>
            </Section>
            <Section num="A.10" title="Third-Party Services & Integrations">
              <UL>
                <li><strong>Razorpay</strong> — Payment processing, subject to Razorpay's terms.</li>
                <li><strong>Cloudinary</strong> — Media storage and delivery, subject to Cloudinary's terms.</li>
                <li><strong>YouTube</strong> — Embedded video playback, subject to YouTube's Terms.</li>
                <li><strong>AI Services</strong> — Content generation and translation via third-party AI providers.</li>
              </UL>
              <P>BrandStage is not responsible for the availability, accuracy, or policies of third-party services.</P>
            </Section>
            <Section num="A.11" title="Service Availability & Modifications">
              <UL>
                <li>Scheduled maintenance with reasonable advance notice.</li>
                <li>Feature modifications to improve the platform.</li>
                <li>Feature discontinuation with at least 30 days' notice where practicable.</li>
                <li>Temporary suspension in case of security threats or emergencies.</li>
              </UL>
            </Section>
            <Section num="A.12" title="Termination & Account Deletion">
              <UL>
                <li><strong>By You:</strong> Delete your account at any time. Data permanently erased within 30 days, except where required by law.</li>
                <li><strong>By BrandStage:</strong> Account suspension or termination for Terms breach, prohibited conduct, or extended inactivity. Notice and remedy opportunity provided where reasonable.</li>
                <li><strong>Effect:</strong> Access ceases immediately. Content retained for a limited period for data export.</li>
              </UL>
            </Section>
            <Section num="A.13" title="Limitation of Liability">
              <UL>
                <li>No liability for indirect, incidental, special, consequential, or punitive damages.</li>
                <li>Total aggregate liability capped at total amount paid in the preceding 12 months.</li>
                <li>No warranty that the platform will be error-free, secure, or uninterrupted.</li>
                <li>Not liable for loss of data, revenue, or opportunities from service interruptions or third-party failures.</li>
              </UL>
            </Section>
            <Section num="A.14" title="Indemnification">
              <P>You agree to indemnify and hold harmless BrandStage, CONTINEX, and their officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the platform, your content, or your violation of these Terms.</P>
            </Section>
            <Section num="A.15" title="Governing Law & Dispute Resolution">
              <UL>
                <li>Governed by and construed in accordance with the laws of India.</li>
                <li>Disputes first addressed through good-faith negotiation.</li>
                <li>Arbitration in Chennai, India, per the Arbitration and Conciliation Act, 1996.</li>
                <li>Either party may seek interim or injunctive relief from a court of competent jurisdiction.</li>
              </UL>
            </Section>
            <Section num="A.16" title="Changes to These Terms">
              <P>Material changes notified via email or prominent platform notice at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.</P>
            </Section>
            <Section num="A.17" title="Contact">
              <P><strong>Email:</strong> operations@brandstage-media.com &nbsp;|&nbsp; <strong>Website:</strong> brandstage-media.com &nbsp;|&nbsp; <strong>Operated by:</strong> CONTINEX</P>
            </Section>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              PART B: PRIVACY POLICY
              ═══════════════════════════════════════════════════════════════ */}
          <div className="mb-8 legal-divider pt-6 mt-8 border-t-2 border-[#C41E3A]/30">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#C41E3A]/30">
              <div className="bg-[#C41E3A]/20 p-2.5 rounded-lg"><Shield className="w-5 h-5 text-[#C41E3A]" /></div>
              <div>
                <h2 className="text-xl font-bold text-white">Part B — Privacy Policy</h2>
                <p className="text-zinc-500 text-xs">DPDP Act 2023 & GDPR Compliant | Last updated: October 2026</p>
              </div>
            </div>

            <Section num="B.1" title="Information We Collect">
              <UL>
                <li><strong>Account Information:</strong> Name, email, password (hashed), company, phone, country.</li>
                <li><strong>Profile Information:</strong> Company details, tagline, headquarters, industry, leadership info, logos, cover images.</li>
                <li><strong>Social Media Links:</strong> LinkedIn, Twitter/X, Instagram, YouTube, website URLs.</li>
                <li><strong>Video Content:</strong> Recorded videos, testimonials, and associated metadata.</li>
                <li><strong>Testimonial Data:</strong> Guest name, organisation, consent record (timestamp, IP), video content.</li>
                <li><strong>Payment & Subscription Data:</strong> Plan, billing period, payment status, invoices. Card details processed by Razorpay — never stored on BrandStage servers.</li>
                <li><strong>Consent Records:</strong> Captured at sign-up, before recording, at checkout, and for marketing communications.</li>
                <li><strong>Event & Invitation Data:</strong> Event details, guest lists, RSVP responses, delivery status.</li>
                <li><strong>Usage & Analytics:</strong> Pages visited, features used, view counts, device type, browser, IP, referral sources.</li>
                <li><strong>Communication Data:</strong> Platform emails and delivery/open status.</li>
              </UL>
            </Section>
            <Section num="B.2" title="Legal Basis for Processing">
              <UL>
                <li><strong>Consent:</strong> Marketing, analytics cookies, testimonials, video recording.</li>
                <li><strong>Contractual Necessity:</strong> Profile hosting, video processing, subscription management.</li>
                <li><strong>Legitimate Interest:</strong> Service improvement, fraud prevention, security, service-related communications.</li>
                <li><strong>Legal Obligation:</strong> Tax, accounting, regulatory requirements.</li>
              </UL>
            </Section>
            <Section num="B.3" title="How We Use Your Information">
              <UL>
                <li><strong>Service Delivery:</strong> Profile management, video branding, email signatures, QR codes.</li>
                <li><strong>Subscription Management:</strong> Payments, invoices, renewals, upgrades, coupons.</li>
                <li><strong>Communication:</strong> Transactional emails, event invitations, marketing (with consent).</li>
                <li><strong>AI-Powered Features:</strong> Profile generation, translations, readiness scoring. Data not used to train AI models.</li>
                <li><strong>Analytics & Improvement:</strong> Usage understanding, feature improvement, engagement measurement.</li>
                <li><strong>Security:</strong> Account protection, suspicious activity detection, Terms enforcement.</li>
              </UL>
            </Section>
            <Section num="B.4" title="Profile Privacy & Link-Only Access">
              <P>Profiles are private by design — not indexed by search engines, not listed in any public directory. Only people with your unique link can view your profile. BrandStage does not distribute profile links without your authorisation.</P>
            </Section>
            <Section num="B.5" title="Video Content, Testimonials & Consent">
              <UL>
                <li>Videos stored securely on Cloudinary. May include BrandStage branding.</li>
                <li>Guest testimonials require explicit consent before recording. Consent records stored per DPDP/GDPR.</li>
                <li>Video deletion within 30 days of request.</li>
                <li>No sale of video content to third parties.</li>
              </UL>
            </Section>
            <Section num="B.6" title="Payment Data">
              <UL>
                <li>All payments via Razorpay (PCI-DSS compliant). No card data stored on BrandStage servers.</li>
                <li>Subscription records retained for accounting/tax compliance.</li>
                <li>Automated PDF invoices generated per transaction.</li>
              </UL>
            </Section>
            <Section num="B.7" title="Data Storage & Security">
              <UL>
                <li><strong>Encryption in Transit:</strong> TLS/HTTPS for all data transmission.</li>
                <li><strong>Secure Cloud Infrastructure:</strong> Role-based access, automated backups, monitoring.</li>
                <li><strong>Password Security:</strong> bcrypt hashing. No plain-text storage.</li>
                <li><strong>Access Controls:</strong> Need-to-know basis with authentication.</li>
                <li><strong>Incident Response:</strong> Notification within 72 hours for data breaches per GDPR/DPDP.</li>
              </UL>
            </Section>
            <Section num="B.8" title="Data Sharing & Third-Party Processors">
              <UL>
                <li><strong>Razorpay</strong> — Payments (India)</li>
                <li><strong>Cloudinary</strong> — Media storage & delivery (US/EU)</li>
                <li><strong>MongoDB Atlas</strong> — Database (Multi-region, encrypted)</li>
                <li><strong>Resend</strong> — Email delivery (US)</li>
                <li><strong>AI Providers</strong> — Content generation (per-request, not retained)</li>
                <li><strong>Google reCAPTCHA</strong> — Bot detection</li>
              </UL>
              <P>No personal data sold to any third party. Disclosure only when required by law.</P>
            </Section>
            <Section num="B.9" title="Your Rights (DPDP Act 2023 / GDPR)">
              <UL>
                <li><strong>Access:</strong> Request a copy of your personal data.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data.</li>
                <li><strong>Deletion:</strong> Request erasure within 30 days (except legal retention).</li>
                <li><strong>Portability:</strong> Request data in structured, machine-readable format.</li>
                <li><strong>Withdraw Consent:</strong> At any time without affecting prior processing.</li>
                <li><strong>Restrict Processing:</strong> Limit data use during complaints.</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interest.</li>
                <li><strong>Complain:</strong> Lodge complaint with Data Protection Board of India or local GDPR authority.</li>
              </UL>
              <P>Contact: operations@brandstage-media.com. Response within 30 days.</P>
            </Section>
            <Section num="B.10" title="Data Retention">
              <UL>
                <li><strong>Active Accounts:</strong> Duration of account.</li>
                <li><strong>Inactive/Expired:</strong> Data retained 12 months for reactivation.</li>
                <li><strong>Deleted Accounts:</strong> Erased within 30 days. Anonymised analytics may be retained.</li>
                <li><strong>Invoices/Payment:</strong> 7 years (tax compliance).</li>
                <li><strong>Videos:</strong> Deleted from cloud within 30 days of request.</li>
                <li><strong>Security Logs:</strong> 180 days rolling (CERT-In).</li>
              </UL>
            </Section>
            <Section num="B.11" title="International Data Transfers">
              <P>Data may be processed in US/EU via cloud providers. Standard Contractual Clauses (SCCs) in place for GDPR/DPDP cross-border compliance.</P>
            </Section>
            <Section num="B.12" title="Children's Data">
              <P>BrandStage is a B2B platform for professionals. Not directed at individuals under 18. Data from minors deleted promptly if discovered.</P>
            </Section>
            <Section num="B.13" title="Grievance Officer (DPDP Act 2023)">
              <P><strong>Continex Tradeline India Private Limited</strong><br/>Primus Building, No. SP-7A, 1st Floor, Guindy Industrial Estate, Chennai - 600032, India<br/>Email: grievance@brandstage-media.com<br/>Response: Within 30 days</P>
            </Section>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              PART C: COOKIE POLICY
              ═══════════════════════════════════════════════════════════════ */}
          <div className="mb-8 legal-divider pt-6 mt-8 border-t-2 border-[#C41E3A]/30">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#C41E3A]/30">
              <div className="bg-[#C41E3A]/20 p-2.5 rounded-lg"><FileText className="w-5 h-5 text-[#C41E3A]" /></div>
              <div>
                <h2 className="text-xl font-bold text-white">Part C — Cookie Policy</h2>
                <p className="text-zinc-500 text-xs">Last updated: October 2026</p>
              </div>
            </div>

            <Section num="C.1" title="Types of Cookies We Use">
              <UL>
                <li><strong>Essential:</strong> <code className="text-xs bg-zinc-800 px-1 rounded">access_token</code> (authentication, 7 days), <code className="text-xs bg-zinc-800 px-1 rounded">cookie_consent</code> (preferences, 1 year).</li>
                <li><strong>Functional:</strong> Cloudinary (media delivery optimisation, session-based).</li>
                <li><strong>Third-Party:</strong> YouTube embeds (Google policy), Razorpay (payment processing, session-based).</li>
              </UL>
              <P>No advertising or marketing trackers are used. Analytics are server-side only.</P>
            </Section>
            <Section num="C.2" title="Managing Cookies">
              <UL>
                <li>Use the cookie consent banner to accept or reject non-essential cookies.</li>
                <li>Adjust browser settings to block or delete cookies.</li>
                <li>Disabling certain cookies may affect functionality.</li>
              </UL>
            </Section>
            <Section num="C.3" title="Your Cookie Rights (GDPR)">
              <UL>
                <li>Access information about cookies used.</li>
                <li>Withdraw consent at any time.</li>
                <li>Request deletion of data collected through cookies.</li>
                <li>Object to processing of your data.</li>
              </UL>
            </Section>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              PART D: COMPLIANCE & DATA PROTECTION SUMMARY
              ═══════════════════════════════════════════════════════════════ */}
          <div className="mb-8 legal-divider pt-6 mt-8 border-t-2 border-[#C41E3A]/30">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#C41E3A]/30">
              <div className="bg-[#C41E3A]/20 p-2.5 rounded-lg"><Scale className="w-5 h-5 text-[#C41E3A]" /></div>
              <div>
                <h2 className="text-xl font-bold text-white">Part D — Compliance & Data Protection Summary</h2>
                <p className="text-zinc-500 text-xs">Internal Reference | Version 2.0</p>
              </div>
            </div>

            <Section num="D.1" title="Company Information">
              <div className="grid md:grid-cols-2 gap-3 text-sm mt-2">
                <div><span className="text-zinc-500">Legal Entity:</span> <span className="text-white ml-1">Continex Tradeline India Pvt Ltd</span></div>
                <div><span className="text-zinc-500">Brand:</span> <span className="text-white ml-1">BrandStage</span></div>
                <div><span className="text-zinc-500">GST:</span> <span className="text-white ml-1">33AADCC0301L1Z9</span></div>
                <div><span className="text-zinc-500">TIN:</span> <span className="text-white ml-1">33061522910</span></div>
                <div className="md:col-span-2"><span className="text-zinc-500">Address:</span> <span className="text-white ml-1">Primus Building, No. SP-7A, 1st Floor, Guindy Industrial Estate, Chennai - 600032, India</span></div>
              </div>
            </Section>

            <Section num="D.2" title="Regulatory Compliance Status">
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-zinc-700"><th className="text-left py-2 text-zinc-400 text-xs">Regulation</th><th className="text-left py-2 text-zinc-400 text-xs">Applicability</th><th className="text-left py-2 text-zinc-400 text-xs">Status</th></tr></thead>
                  <tbody className="text-zinc-300">
                    <tr className="border-b border-zinc-800"><td className="py-2 text-white">India DPDP Act 2023</td><td className="py-2">Primary</td><td className="py-2"><span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs legal-badge">Framework Implemented</span></td></tr>
                    <tr className="border-b border-zinc-800"><td className="py-2 text-white">IT Act 2000 (India)</td><td className="py-2">Primary</td><td className="py-2"><span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs legal-badge">Framework Implemented</span></td></tr>
                    <tr className="border-b border-zinc-800"><td className="py-2 text-white">GDPR (EU)</td><td className="py-2">If serving EU</td><td className="py-2"><span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded text-xs legal-badge">Applicable Where Relevant</span></td></tr>
                    <tr className="border-b border-zinc-800"><td className="py-2 text-white">PCI-DSS</td><td className="py-2">Payments</td><td className="py-2"><span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs legal-badge">Via Razorpay</span></td></tr>
                    <tr><td className="py-2 text-white">GST Compliance</td><td className="py-2">Tax</td><td className="py-2"><span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs legal-badge legal-green">Registered</span></td></tr>
                  </tbody>
                </table>
              </div>
            </Section>

            <Section num="D.3" title="Product-Specific Disclaimers">
              <div className="space-y-3 text-sm mt-2">
                {[
                  { title: 'AI-Assisted Content', text: 'AI-assisted content is generated from information supplied by the profile owner. The profile owner is responsible for reviewing and approving its accuracy before publication. AI-generated content is clearly labelled.' },
                  { title: 'BrandStage Verified Badge', text: 'Verification establishes specified identity, organisation association and/or content provenance checks. It does not constitute an endorsement, credit assessment, certification of capability, or guarantee of products or services.' },
                  { title: 'Digital Health Score (Pro)', text: 'An indicative assessment based on defined digital-presence criteria. Not an SEO guarantee, credit rating, certification, or assurance of commercial performance. Scores are for guidance only.' },
                  { title: 'Video Testimonials & Contributor Consent', text: 'Testimonials represent the personal views of the contributor at the time of recording. BrandStage does not independently verify claims made. All contributors provide explicit consent before recording. Contributors may request removal at any time.' },
                  { title: 'BrandStage Connect (Coming Soon)', text: 'Opportunities and introductions are for business discovery only. BrandStage acts solely as a facilitator. Publication does not constitute endorsement, due diligence, or assurance of suitability. Participants must undertake their own legal, financial, and commercial assessment. This feature is not yet live.' },
                  { title: 'Profile Owner Content Warranty', text: 'By publishing a profile, the owner warrants that all information is accurate, they have all necessary rights and permissions, the content does not infringe third-party rights, and they will promptly update or remove inaccurate content.' },
                ].map((d, i) => (
                  <div key={i} className="bg-zinc-800/50 rounded-lg p-3 legal-section">
                    <p className="text-white font-semibold text-xs mb-1">{d.title}</p>
                    <p className="text-zinc-400 text-xs leading-relaxed">{d.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section num="D.4" title="CERT-In Cybersecurity Compliance">
              <UL>
                <li><strong>Incident Reporting:</strong> Qualifying incidents reported to CERT-In within statutory timelines.</li>
                <li><strong>Log Management:</strong> ICT system logs maintained for 180 days with time synchronisation.</li>
                <li><strong>Access Controls:</strong> Role-based with least-privilege principles.</li>
                <li><strong>Security Assessments:</strong> Regular reviews conducted. VAPT audits planned for enterprise scale.</li>
              </UL>
            </Section>

            <Section num="D.5" title="Data Breach Notification Policy">
              <UL>
                <li><strong>CERT-In:</strong> Notified within 6 hours for qualifying cyber incidents.</li>
                <li><strong>Data Protection Authority:</strong> Notified as required under applicable law.</li>
                <li><strong>User Notification:</strong> Affected individuals notified within 72 hours where required.</li>
              </UL>
            </Section>

            <Section num="D.6" title="Grievance & Incident Handling">
              <UL>
                <li><strong>Privacy & Data Protection:</strong> operations@brandstage-media.com — Response within 30 days.</li>
                <li><strong>Content, Copyright & IP:</strong> operations@brandstage-media.com — Acknowledgment within 48 hours, resolution within 15 days.</li>
                <li><strong>Security Incidents:</strong> operations@brandstage-media.com — Immediate triage per severity.</li>
                <li><strong>Grievance Officer (DPDP):</strong> Continex Tradeline India Pvt Ltd, Chennai — statutory resolution within 30 days.</li>
              </UL>
            </Section>
          </div>

          {/* Document footer */}
          <div className="text-center text-zinc-500 text-xs pt-4 border-t border-zinc-800 mt-6">
            <p>This document consolidates all published legal documents of BrandStage as of October 2026.</p>
            <p className="mt-1">For the latest versions, visit brandstage-media.com/legal/terms, /legal/privacy, /cookies, and /legal/compliance.</p>
            <p className="mt-1">Operated by: Continex Tradeline India Private Limited | Document version: 2.0</p>
          </div>

          {/* PDF-only footer */}
          <div className="hidden pdf-footer" style={{ marginTop: '20px' }}>
            <p><strong>BrandStage — Complete Legal & Compliance Document</strong></p>
            <p>Confidential — For Internal Use | Version 2.0 | October 2026</p>
            <p>Continex Tradeline India Private Limited | operations@brandstage-media.com</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6 pdf-hide">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} BrandStage. Powered by CONTINEX.</p>
        </div>
      </footer>
    </div>
  );
}
